import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdOndemandVideo,
    MdOutlineAssessment,
    MdOutlineQuiz
} from 'react-icons/md';
import moment from 'moment';
import Discord from 'assets/vectors/Discord.svg';
import Notion from 'assets/vectors/Notion.svg';
import CoursePlaceholderImage from 'assets/vectors/CoursePlaceholder.svg';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import { Course } from 'global/types';
import { setError } from 'store/error';
import './index.css';

type MyCoursesProps = {
    courses: Course[];
};

function MyCourses({ courses }: MyCoursesProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const today = new Date();

    const startCourse = (courseUrl: string) => {
        axios.post(`courses/classroom/${courseUrl}/start/`)
            .then(res => {
                navigate(`/courses/${courseUrl}`);
            })
            .catch(error => {
                // console.log(error);
                if (error.response) {
                    dispatch(setError({ message: error.response.data.message }));
                }
            });
    }

    if (!courses) return <></>;

    return (
        <>
            <Helmet
                title='My Courses - The SAT Crash Course'
            />
            {courses.map(course => (     
                <div className='myCourses__course'>
                    <img
                        className='myCourses__vector'
                        src={course.image
                            ? course.image
                            : CoursePlaceholderImage
                        }
                        alt={course.name}
                    />
                    <div className='myCourses__content'>
                        <div className='myCourses__columnLeft'>
                            <div className='myCourses__courseInfo'>
                                <h2 className='myCourses__courseTitle'>
                                    {course.name}
                                </h2>
                                {course.endDate && new Date(course.endDate) < today
                                ?   <p className='myCourses__textGray'>
                                        Course has ended
                                    </p>
                                :   course.startDate && new Date(course.startDate) <= today
                                ?   <p className='myCourses__textGray'>
                                        {moment(course.startDate).format('ll')} - {moment(course.endDate).format('ll')}
                                    </p>
                                :   <p className='myCourses__textGray'>
                                       Course not started yet 
                                    </p>
                                }
                                <p className='myCourses__textRed'>
                                    * Once you start the course, you will have {Math.round(moment.duration(course.period).asMonths())} Months to finish it. After {Math.round(moment.duration(course.period).asMonths())} Months, this course will not be accessible.
                                </p>
                            </div>
                            {course.preTest && !course.preTest.isTaken
                            ?   <div className='myCourses__courseInfo'>
                                    <p className='myCourses__text'>
                                        Take the level test now to start the course!
                                    </p>
                                    <p className='myCourses__textGray'>
                                        <MdOutlineAssessment className='myCourses__courseIcon' />
                                        {course.name} Level Test
                                    </p>
                                </div>
                            :   <div className='myCourses__courseInfo'>
                                    {course.startDate && course.endDate && new Date(course.endDate) >= today && (course.nextQuiz
                                    ?   <p className='myCourses__text'>
                                            {course.nextQuiz.lectureGroup.name}
                                        </p>
                                    :   course.nextLecture
                                    ?   <p className='myCourses__text'>
                                            {course.nextLecture.lectureGroup.name}
                                        </p>
                                    :   <></>
                                    )}
                                    {course.startDate && course.endDate && new Date(course.endDate) >= today && (course.nextLecture && course.nextQuiz
                                    ?   (course.nextLecture.lectureGroup.groupNumber > course.nextQuiz.lectureGroup.groupNumber
                                        ?   <p className='myCourses__textGray'>
                                                <MdOutlineQuiz className='myCourses__courseIcon' />
                                                {course.nextQuiz.name}
                                            </p>
                                        :   <p className='myCourses__textGray'>
                                                <MdOndemandVideo className='myCourses__courseIcon' />
                                                {course.nextLecture.name}
                                            </p>
                                        )
                                    :   course.nextLecture
                                    ?   <p className='myCourses__textGray'>
                                            <MdOndemandVideo className='myCourses__courseIcon' />
                                            {course.nextLecture.name}
                                        </p>
                                    :   course.nextQuiz
                                    ?   <p className='myCourses__textGray'>
                                            <MdOutlineQuiz className='myCourses__courseIcon' />
                                            {course.nextQuiz.name}
                                        </p>
                                    :   <></>
                                    )}
                                    {course.endDate && course.endDate < today &&
                                        <p className='myCourses__textGray'>
                                            Course has expired
                                        </p>
                                    }
                                </div>
                            }
                        </div>
                        <div className='myCourses__columnRight'>
                            <table className='myCourses__linkTable'>
                                <tbody>
                                    {course.communityUrl &&
                                        <tr>
                                            <th className='myCourses__text'>
                                                Check out the community
                                            </th>
                                            <td>
                                                <a
                                                    href={course.communityUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    <img src={Discord} width={100} />
                                                </a>
                                            </td>
                                        </tr>
                                    }
                                    {course.courseTutorialUrl &&
                                        <tr>
                                            <th className='myCourses__text'>
                                                Pre-course tutorials
                                            </th>
                                            <td>
                                                <a
                                                    href={course.courseTutorialUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    <img src={Notion} width={80} />
                                                </a>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            <div className='myCourses__buttonGroup'>
                                {course.preTest && (course.preTest.isTaken
                                ?   <ButtonLink 
                                        className='myCourses__button'
                                        to={`/courses/report/${course.preTest.studentTestId}`}
                                        secondary
                                    >
                                        LEVEL TEST RESULTS
                                    </ButtonLink>   
                                :   <Button
                                        className='myCourses__button'
                                        onClick={() => navigate(`/test/${course.preTest.studentTestId}`)}
                                    >
                                        TAKE LEVEL TEST
                                    </Button>
                                )}
                                {!course.nextLecture && !course.nextQuiz && course.postTest && (course.postTest.isTaken
                                ?   <ButtonLink 
                                        className='myCourses__button'
                                        to={`/courses/report/${course.postTest.studentTestId}`}
                                        secondary
                                    >
                                        POST TEST RESULTS
                                    </ButtonLink>   
                                :   course.preTest && !course.preTest.isTaken
                                ?   <></>
                                :   <Button
                                        className='myCourses__button'
                                        onClick={() => navigate(`/test/${course.postTest.studentTestId}`)}
                                    >
                                        TAKE POST TEST
                                    </Button>
                                )}
                                {course.endDate && new Date(course.endDate) < today
                                ?   (course.syllabusUrl
                                    ?   <a
                                            className='myCourses__notes'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            href={course.syllabusUrl}
                                        >
                                            VIEW COURSE NOTES
                                        </a>
                                    :   <></>
                                    )
                                :   course.startDate && new Date(course.startDate) <= today
                                ?   <Button
                                        className='myCourses__button'
                                        onClick={() => navigate(`/courses/${course.urlSlug}`)}
                                    >
                                        CONTINUE STUDYING
                                    </Button>
                                :   course.preTest && !course.preTest.isTaken
                                ?   <></>
                                :    <Button
                                        className='myCourses__button'
                                        onClick={() => startCourse(course.urlSlug)}
                                    >
                                        START STUDYING
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
}

export default MyCourses;